import { Injectable, inject } from '@angular/core';
import {StorageService} from './storage.service';
import {PrimeNG} from 'primeng/config';
import {UserContextService} from './user-context.service';
import {TranslateService} from '@ngx-translate/core';
import {AnalyticsService} from './analytics.service';
import {APP_CONFIG} from '../config/app-config.token';
import {AppConfig} from '../config/app-config.model';
import {filter, take} from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class TranslationService {
  private translateService = inject(TranslateService);
  private storage = inject(StorageService);
  private primeng = inject(PrimeNG);
  private userContext = inject(UserContextService);
  private analyticsService = inject(AnalyticsService);
  private config = inject<AppConfig>(APP_CONFIG);

  private initialized = false;

  init(): void {
    // Set initial translations (may use stored/browser lang if user not authenticated yet)
    this.setTranslations();
    this.initialized = true;

    // For modern auth: Subscribe to user profile changes to update locale after authentication
    this.userContext.userProfile$
      .pipe(filter(profile => profile !== null))
      .subscribe({
        next: profile => {
          const userLocale = profile?.locale;
          if (userLocale) {
            const targetLang = userLocale.indexOf('fr') > -1 ? 'fr' : 'nl';
            const currentLang = this.storage.getLanguage();
            if (currentLang !== targetLang) {
              this.translate(targetLang);
            }
          }
        },
        error: err => console.error('[TranslationService] Profile subscription error:', err)
      });
  }

  /**
   * Change the application language and update all translations including PrimeNG components
   */
  changeLanguage(lang: string): void {
    this.translate(lang, true);
  }

  /**
   * Toggle between 'nl' and 'fr' languages
   */
  toggleLanguage(): void {
    const current = this.storage.getLanguage() || 'nl';
    const next = current === 'nl' ? 'fr' : 'nl';
    this.translate(next, true);
  }

  private setTranslations() {
    const lang = this.storage.getLanguage();
    const browserLang = this.translateService.getBrowserLang();
    const userLocale = this.userContext.getLocale();

    // Check if user locale is available (after authentication)
    if (userLocale) {
      if (userLocale.indexOf('fr') > -1) {
        this.translate('fr');
      } else {
        this.translate('nl');
      }
    } else if (typeof lang !== 'undefined' && lang !== null) {
      this.translate(lang);
    } else if (browserLang === 'nl' || browserLang === 'fr') {
      this.translate(browserLang);
    } else {
      this.translate('nl');
    }
  }

  private translate(lang: string, trackEvent: boolean = false) {
    const previousLang = this.storage.getLanguage();
    this.translateService.setDefaultLang(lang);
    this.storage.setLanguage(lang);
    this.translateService.use(lang);
    this.translateService.get('primeng').pipe(take(1)).subscribe({
      next: res => this.primeng.setTranslation(res),
      error: err => console.error('[TranslationService] PrimeNG translation error:', err)
    });

    // Track language change (only for user-initiated changes)
    if (trackEvent && previousLang && previousLang !== lang) {
      this.analyticsService.trackEvent('language_change', {
        from: previousLang,
        to: lang
      });
    }
  }
}
